// Small DOM helpers. Text always goes in through textContent; only the static icon strings use innerHTML.
import { icon } from "./icons.js";

export function h(tag, attrs = {}, ...children) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v === false || v == null) continue;
    if (k === "class") el.className = v;
    else if (k === "text") el.textContent = v;
    else if (k === "icon") el.insertAdjacentHTML("afterbegin", icon(v));
    else if (k.startsWith("on") && typeof v === "function") el.addEventListener(k.slice(2), v);
    else if (k === "dataset") Object.assign(el.dataset, v);
    else el.setAttribute(k, v === true ? "" : v);
  }
  for (const c of children.flat()) {
    if (c == null || c === false) continue;
    el.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
  return el;
}

export function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  const text = (parts[0]?.[0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "");
  return text.toUpperCase() || "·";
}

export function firstName(name) {
  return String(name || "").trim().split(/\s+/)[0] || "";
}

let toastBox;
const keyed = new Map();
// A toast with a `key` replaces the one already showing under that key, so a burst of chat or knocks is one updating
// notice, not a column of pills over the video (audit U9). Without a key, each toast stands alone, as before.
export function toast(text, ms = 3200, key = "") {
  if (!toastBox) {
    toastBox = h("div", { class: "toasts", role: "status", "aria-live": "polite" });
    document.body.append(toastBox);
  }
  const was = key && keyed.get(key);
  const t = was && was.isConnected ? was : h("div", { class: "toast" });
  t.textContent = text;
  if (t !== was) toastBox.append(t);
  clearTimeout(t._timer);
  t._timer = setTimeout(() => { t.remove(); if (key && keyed.get(key) === t) keyed.delete(key); }, ms);
  if (key) keyed.set(key, t);
}
// Everything the call said goes with the call: an end screen must not carry "Asha left" under "Asha ended the meeting".
export function clearToasts() {
  for (const t of keyed.values()) clearTimeout(t._timer);
  keyed.clear();
  toastBox?.replaceChildren();
}

// A popover anchored to a control. Closes on a click outside, Escape, or when an item is chosen.
let openMenu = null;
export function closeMenu() {
  if (openMenu) {
    openMenu.el.remove();
    document.removeEventListener("pointerdown", openMenu.away, true);
    document.removeEventListener("keydown", openMenu.esc, true);
    openMenu.anchor?.setAttribute("aria-expanded", "false");
    openMenu = null;
  }
}
export function showMenu(anchor, content, { label = "Menu" } = {}) {
  const wasSame = openMenu && openMenu.anchor === anchor;
  closeMenu();
  if (wasSame) return null;
  const el = h("div", { class: "menu", role: "menu", "aria-label": label }, content);
  document.body.append(el);
  const r = anchor.getBoundingClientRect();
  const w = el.offsetWidth, hgt = el.offsetHeight;
  const left = Math.min(Math.max(8, r.left + r.width / 2 - w / 2), window.innerWidth - w - 8);
  const top = r.top - hgt - 10 > 8 ? r.top - hgt - 10 : Math.min(r.bottom + 10, window.innerHeight - hgt - 8);
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
  const away = (e) => { if (!el.contains(e.target) && !anchor.contains(e.target)) closeMenu(); };
  const esc = (e) => { if (e.key === "Escape") { closeMenu(); anchor.focus(); } };
  document.addEventListener("pointerdown", away, true);
  document.addEventListener("keydown", esc, true);
  anchor.setAttribute("aria-expanded", "true");
  openMenu = { el, anchor, away, esc };
  el.querySelector("button")?.focus({ preventScroll: true });
  return el;
}

export function menuItem(text, onClick, { iconName, checked = false, danger = false } = {}) {
  const b = h("button", { class: "item" + (danger ? " is-danger" : ""), type: "button", role: "menuitem" });
  if (iconName) b.insertAdjacentHTML("beforeend", icon(iconName));
  b.append(h("span", { text }));
  if (checked) b.insertAdjacentHTML("beforeend", icon("check", "check"));
  b.addEventListener("click", () => { closeMenu(); onClick(); });
  return b;
}

export function modal(title, body, actions) {
  return new Promise((resolve) => {
    const done = (v) => { scrim.remove(); document.removeEventListener("keydown", esc, true); resolve(v); };
    const esc = (e) => { if (e.key === "Escape") done(null); };
    const row = h("div", { class: "row" }, actions.map((a) =>
      h("button", { class: a.class || "btn btn-ghost", type: "button", onclick: () => done(a.value) }, a.text)));
    const box = h("div", { class: "modal", role: "dialog", "aria-modal": "true", "aria-label": title }, h("h3", { text: title }), body, row);
    const scrim = h("div", { class: "scrim", onclick: (e) => { if (e.target === scrim) done(null); } }, box);
    document.body.append(scrim);
    document.addEventListener("keydown", esc, true);
    box.querySelector(".row button:last-child")?.focus();
  });
}
